import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';

const PROP_KEYS_TO_LOG = [
    'GLOBAL_ID',
    'source',
    'center',
    'radiusMeters'
];

const getPropsToLog = (feature: Feature) => {
    const props = feature.properties;
    // return props;
    return PROP_KEYS_TO_LOG.filter(k => props?.[k]).reduce((p, k) => ({ ...p, [k]: props![k] }), {});
};

/**
 * precision to meters
 * 6 - 0.11112
 * 5 - 1.1112
 * 4 - 11.112
 */
const laancSafeTruncatePrecision = 6;

const truncateFeature = (feature: Feature<Polygon | MultiPolygon>, { allowMutation = false } = {}): Feature<Polygon | MultiPolygon> => {
    const truncatedFeature = turf.truncate(feature, { precision: laancSafeTruncatePrecision, mutate: allowMutation });
    return truncatedFeature;
};

export interface IIntersectOptions {
    truncate?: boolean;
    allowMutation?: boolean;
}

export class FeatureIntersector {

    getIntersection = (feature1: Feature<Polygon | MultiPolygon>, feature2: Feature<Polygon | MultiPolygon>, { truncate = false, allowMutation = false }: IIntersectOptions = {}): Feature<Polygon | MultiPolygon> | undefined => {
        if (truncate) {
            feature1 = truncateFeature(feature1, { allowMutation });
            feature2 = truncateFeature(feature2, { allowMutation });
        }


        try {
            return this.removeIntersectionfNoise(turf.intersect(feature1, feature2));

        } catch (e1: any) {
            if (feature2.geometry.type === 'MultiPolygon' && e1.message?.includes('Infinite loop when putting segment endpoints in a priority queue')) {
                try {
                    // The underlying library polygon-clipping can throw this exception for large MultiPolygons.
                    // (see https://github.com/mfogel/polygon-clipping/blob/203699f5f39fedbdaa1b44ed2ad75f79309d8362/dist/polygon-clipping.cjs.js#L1731)
                    // We've determined that a potential workaround is to intersect one polygon at a time, then union the results.
                    return this.removeIntersectionfNoise(this.intersectByPolygon(feature1, feature2 as Feature<MultiPolygon>));

                } catch (e2: any) {
                    const propsToLog = { f1Props: getPropsToLog(feature1), f2Props: getPropsToLog(feature2) };
                    e2.message = `intersectByPolygon(f1, f2) failed: ${e2.message}, ${JSON.stringify(propsToLog)}`;
                    throw e2;
                }

            } else {
                const propsToLog = { f1Props: getPropsToLog(feature1), f2Props: getPropsToLog(feature2) };
                e1.message = `turf.intersect(f1, f2) failed: ${e1.message}, ${JSON.stringify(propsToLog)}`;
                throw e1;
            }
        }
    }

    // TODO: this is [mostly] duplicated with GISDiffer
    private removeIntersectionfNoise = (diff: any) => {
        if (diff && diff.geometry.type === 'MultiPolygon') {
            diff.geometry.coordinates = diff.geometry.coordinates.filter((polygonCoords: any) => {
                // NOTE: would love to detect the shape.... eg 100sq meters that is only .5 meters wide... is noise
                return turf.area({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Polygon',
                        coordinates: polygonCoords
                    }
                } as any) >= 10;
            });

            // we may have just filtered out all the polygons b/c they were all small
            if (diff.geometry.coordinates.length === 0) {
                return undefined;
            }
        }

        // NOTE: we may want to also filter out a single small polygon... but part of the symptom
        // is a MultiPolgon with lots of very small polygons
        return diff;
    }

    private intersectByPolygon = (feature1: Feature<Polygon | MultiPolygon>, feature2: Feature<MultiPolygon>) => {
        const intersections: Feature<Polygon | MultiPolygon>[] = [];

        for (let i = 0; i < feature2.geometry.coordinates.length; i++) {
            const polygon: Feature<Polygon> = {
                type: 'Feature',
                properties: { ...feature2.properties, polygonIndex: i },
                geometry: {
                    type: 'Polygon',
                    coordinates: feature2.geometry.coordinates[i]
                }
            };
            const intersection = turf.intersect(feature1, polygon);
            if (intersection && turf.area(intersection) > 10) {
                intersections.push(intersection);
            }
        }

        if (intersections.length === 0) {
            return undefined;
        }

        if (intersections.length === 1) {
            return intersections[0];
        }

        let merged: Feature<Polygon | MultiPolygon> | undefined;
        for (const polygon of intersections) {
            if (!merged) {
                merged = polygon;
            } else {
                merged = turf.union(merged, polygon) || undefined;
            }
        }

        return merged;
    };
}
